import { EMPTY_STR } from "@/constants/string";

/**
 * Replaces placeholders in a template string with corresponding values from an object.
 *
 * @param {string} template - The string containing placeholders (e.g., "Hello, {{name}}!").
 * @param {object} replaces - An object where keys are placeholders and values are replacements (e.g., { name: "Alice" }).
 * @returns {string} - The resulting string with placeholders replaced by actual values.
 */
export const replaceStr = (template: string, replaces: object): string => {
  return Object.entries(replaces).reduce((cur, [key, value]) => (value !== undefined ? cur.replace(`{{${key}}}`, value.toString()) : cur), template);
};

export const getFullAddress = (addressObject: { province?: string | null; municipality?: string | null; address?: string | null }): string => {
  const { province, municipality, address } = addressObject;
  const fullAddress = [province ?? [], municipality ?? [], address ?? []].flat().join("").trim();
  return fullAddress.length ? fullAddress : EMPTY_STR.TEXT;
};

/**
 * Converts a value to a string. Returns an empty string if the value is not a string.
 *
 * @param {unknown} data - The value to convert.
 * @returns {string} - The string representation of the value, or an empty string if the value is not a string.
 */
export const toString = (data: unknown): string => {
  if (typeof data === "string") return data;
  return "";
};

/**
 * Truncates a string with an ellipsis if it exceeds a given length
 * @param {string} str - The input string to truncate
 * @param {number} maxLength - The maximum allowed length for the string
 * @returns {string} - The truncated string with an ellipsis (if needed)
 */
export const truncateWithEllipsis = (str: string, maxLength: number): string => {
  // Check if the string needs truncation
  if (str.length <= maxLength) {
    return str;
  }
  // Truncate the string and add an ellipsis
  return str.slice(0, maxLength - 3) + "...";
};

const HYPERLINK_STYLE = "color:#185DE3;text-decoration:underline;word-break:break-all";

const applyAnchorAttributes = (aTag: string): string => {
  const openingEnd = aTag.indexOf(">");
  let opening = aTag.slice(0, openingEnd);
  const rest = aTag.slice(openingEnd);

  if (/\bstyle="/i.test(opening)) opening = opening.replace(/\bstyle="/i, `style="${HYPERLINK_STYLE};`);
  else opening += ` style="${HYPERLINK_STYLE}"`;

  if (/\btarget="/i.test(opening)) opening = opening.replace(/\btarget="[^"]*"/i, 'target="_blank"');
  else opening += ' target="_blank"';

  if (/\brel="/i.test(opening)) opening = opening.replace(/\brel="[^"]*"/i, 'rel="noopener noreferrer"');
  else opening += ' rel="noopener noreferrer"';

  return opening + rest;
};

export const convertUrlsToHyperlinks = (html: string): string => {
  return html.replace(/(<a\b[^>]*>[\s\S]*?<\/a>)|(https?:\/\/[^\s<>"']+)/g, (match, aTag) => {
    if (aTag !== undefined) return applyAnchorAttributes(aTag);
    return `<a href="${match}" target="_blank" rel="noopener noreferrer" style="${HYPERLINK_STYLE}">${match}</a>`;
  });
};
